import { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Search, ImageIcon, Calendar, Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { fetchReceipts, setSelectedReceipt, type FetchReceiptsParams } from '@/features/receipts/receiptsSlice';
import { fetchCategories } from '@/features/categories/categoriesSlice';
import type { Receipt } from '@/lib/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useReceiptBlob } from '@/hooks/useReceiptBlob';
import { cn } from '@/lib/utils';
import ReceiptUploadDialog from '@/components/receipts/ReceiptUploadDialog';
import ReceiptDetailSheet from '@/components/receipts/ReceiptDetailSheet';
import ReceiptInsights from '@/components/receipts/ReceiptInsights';
import ReceiptShares from '@/components/receipts/ReceiptShares';

function ReceiptThumbnail({ receipt }: { receipt: Receipt }) {
  // useReceiptBlob handles PDF early-return, IntersectionObserver lazy fetching,
  // axios-with-credentials, blob URL lifecycle, and abort-on-unmount.
  // Ref: https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
  const { url, loading, error, ref } = useReceiptBlob(
    receipt._id,
    receipt.mimeType,
    { lazy: true, rootMargin: '200px' }
  );
  const isPdf = receipt.mimeType === 'application/pdf';

  if (isPdf) {
    return (
      <div className="flex h-full items-center justify-center bg-muted/50">
        <div className="text-center">
          <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-1 text-[10px] text-muted-foreground">PDF</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="relative h-full w-full">
      {(loading || (!url && !error)) && (
        <Skeleton className="absolute inset-0" data-testid={`receipt-thumbnail-skeleton-${receipt._id}`} />
      )}
      {error && (
        <div className="flex h-full items-center justify-center bg-muted/50">
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}
      {url && (
        <img
          src={url}
          alt={receipt.originalFilename}
          data-testid={`receipt-thumbnail-img-${receipt._id}`}
          className={cn('h-full w-full object-cover transition-opacity duration-300 opacity-100')}
        />
      )}
    </div>
  );
}

export default function Receipts() {
  useDocumentTitle('Receipts');
  const dispatch = useAppDispatch();
  const { fmt } = useCurrency();
  const { receipts, loading, total, page, totalPages } = useAppSelector((s) => s.receipts);
  const { categories } = useAppSelector((s) => s.categories);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReceipt, setDetailReceipt] = useState<Receipt | null>(null);

  // Filters
  const [merchantSearch, setMerchantSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const hasFetched = useRef(false);

  const buildParams = useCallback(
    (pg = 1): FetchReceiptsParams => {
      const params: FetchReceiptsParams = { page: pg, limit: 20, sort: '-createdAt' };
      if (merchantSearch.trim()) params.merchant = merchantSearch.trim();
      if (categoryFilter && categoryFilter !== '__all__') params.category = categoryFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      return params;
    },
    [merchantSearch, categoryFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(fetchReceipts(buildParams()));
    dispatch(fetchCategories());
  }, [dispatch, buildParams]);

  const handleSearch = () => {
    dispatch(fetchReceipts(buildParams(1)));
  };

  const handlePageChange = (newPage: number) => {
    dispatch(fetchReceipts(buildParams(newPage)));
  };

  const openDetail = (receipt: Receipt) => {
    setDetailReceipt(receipt);
    dispatch(setSelectedReceipt(receipt));
    setDetailOpen(true);
  };

  const handleUploaded = () => {
    dispatch(fetchReceipts(buildParams(1)));
  };

  const renderGrid = () => {
    if (loading && receipts.length === 0) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="aspect-[3/4] w-full rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      );
    }

    if (receipts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center" data-testid="receipts-empty">
          <ImageIcon className="mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium">No receipts yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Upload your first receipt to get started</p>
          <Button className="mt-4 min-h-[44px]" onClick={() => setUploadOpen(true)}>
            <Upload className="mr-2 h-4 w-4" /> Upload Receipt
          </Button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="receipts-grid">
        {receipts.map((receipt) => (
          <button
            key={receipt._id}
            className="group relative flex flex-col overflow-hidden rounded-lg border bg-card text-left transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[44px]"
            onClick={() => openDetail(receipt)}
            data-testid={`receipt-card-${receipt._id}`}
          >
            {/* Thumbnail */}
            <div className="relative aspect-[3/4] overflow-hidden bg-muted">
              <ReceiptThumbnail receipt={receipt} />
              {/* Status indicators */}
              <div className="absolute bottom-1 right-1 flex gap-1">
                {receipt.transactionId && (
                  <Badge className="h-5 text-[10px] bg-blue-500/90">Linked</Badge>
                )}
                {receipt.suggestedCategory && !receipt.categorizedAt && (
                  <Badge className="h-5 text-[10px] bg-amber-500/90">Suggestion</Badge>
                )}
              </div>
            </div>
            {/* Info */}
            <div className="flex-1 p-2">
              <p className="text-xs font-medium break-words line-clamp-2">
                {receipt.metadata.merchant || receipt.originalFilename}
              </p>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">
                  {new Date(receipt.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
                {receipt.metadata.total != null && (
                  <span className="text-xs font-semibold">{fmt(receipt.metadata.total)}</span>
                )}
              </div>
              {receipt.tags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {receipt.tags.slice(0, 2).map((tag) => (
                    <Badge key={tag} variant="outline" className="h-4 text-[10px] px-1">
                      {tag}
                    </Badge>
                  ))}
                  {receipt.tags.length > 2 && (
                    <span className="text-[10px] text-muted-foreground">+{receipt.tags.length - 2}</span>
                  )}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 md:p-6" data-testid="receipts-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Receipts</h1>
          {total > 0 && (
            <p className="text-sm text-muted-foreground">{total} receipt{total !== 1 ? 's' : ''}</p>
          )}
        </div>
        <Button onClick={() => setUploadOpen(true)} className="min-h-[44px]" data-testid="upload-receipt-btn">
          <Upload className="mr-2 h-4 w-4" /> Upload
        </Button>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all" className="min-h-[44px]" data-testid="tab-all-receipts">All Receipts</TabsTrigger>
          <TabsTrigger value="insights" className="min-h-[44px]" data-testid="tab-insights">Insights</TabsTrigger>
          <TabsTrigger value="shares" className="min-h-[44px]" data-testid="tab-shares">Shared Links</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Filter bar */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={merchantSearch}
                  onChange={(e) => setMerchantSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Search by merchant..."
                  className="pl-9 min-h-[44px]"
                  data-testid="receipt-search"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="min-h-[44px] min-w-[44px]"
                onClick={() => setShowFilters((v) => !v)}
                data-testid="receipt-filter-toggle"
              >
                <Filter className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                className="min-h-[44px]"
                onClick={handleSearch}
                data-testid="receipt-search-btn"
              >
                Search
              </Button>
            </div>

            {showFilters && (
              <div className="flex flex-wrap gap-2 rounded-md border bg-muted/30 p-3" data-testid="receipt-filters">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-36"
                    placeholder="From"
                    data-testid="receipt-date-from"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-36"
                    placeholder="To"
                    data-testid="receipt-date-to"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-9 w-40" data-testid="receipt-category-filter">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id || cat.name} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Grid */}
          {renderGrid()}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2" data-testid="receipt-pagination">
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                disabled={page <= 1 || loading}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                disabled={page >= totalPages || loading}
                onClick={() => handlePageChange(page + 1)}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Next'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="insights">
          <ReceiptInsights />
        </TabsContent>

        <TabsContent value="shares">
          <ReceiptShares />
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <ReceiptUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={handleUploaded} />

      {/* Detail sheet */}
      <ReceiptDetailSheet receipt={detailReceipt} open={detailOpen} onOpenChange={setDetailOpen} />
    </div>
  );
}
